import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import moment from "moment";

import { config, version } from "../site-local";
import {
    Address,
    Agent,
    Attachment,
    Cancellation,
    Company,
    ConsultationDate,
    ConsultationProcess,
    ConsultationProcessState,
    CreateDocumentResult,
    Customer,
    Decision,
    Demand,
    Division,
    DivisionEntry,
    Document,
    Event,
    Exemption,
    FileData,
    FinalizeResult,
    FoundConsultationProcess,
    GlobalOccasion,
    GlobalOccasionEntry,
    GlobalOccasionSection,
    Hint,
    Individual,
    OccasionOccasionEntry,
    OccasionOccasionEntryCollection,
    Offer,
    Participant,
    Person,
    ProductOccasionEntry,
    ProductOccasionSection,
    Protocol,
    Recommendation,
    SearchHint,
    SearchResult,
    SignatureFieldMetadata,
    Textblock,
    TrackingText,
    UpdateResult,
} from "../data/model";
import { LoggerService } from "./logger.service";

export class BackendService {
    protected serviceUrl = config.ZBDSERVICEURL;

    protected reqOpts: {
        headers?: HttpHeaders;
        observe: "body";
        responseType: "json";
    };

    protected logger = inject(LoggerService);

    constructor(
        protected http: HttpClient,
        protected trace = false,
    ) {
        const headers = new HttpHeaders()
            .append("Content-Type", "application/json")
            .append("Accept", "application/json");
        this.reqOpts = {
            headers: headers,
            observe: "body",
            responseType: "json",
        };
    }

    protected log(method: string, target: string) {
        this.logger.log("%s %s", method, target);
    }

    public getAddress(addressId: number): Observable<Address> {
        if (!addressId) return;
        if (this.trace) this.log("GET", `Addresses`);
        const url = `${this.serviceUrl}/Addresses(${addressId})`;
        return this.http.get<Address>(url, this.reqOpts);
    }

    public updateAddress(address: Address): Observable<Address> {
        if (!address || !address.Id)
            return Observable.create((obs) => obs.next(address));
        if (this.trace) this.log("PUT", `Addresses(${address.Id})`);
        const url = `${this.serviceUrl}/Addresses(${address.Id})`;
        return this.http.put<Address>(
            url,
            JSON.stringify(address),
            this.reqOpts,
        );
    }

    public deleteAddress(address: Address): Observable<Address> {
        if (!address || !address.Id) return;
        if (this.trace) this.log("DELETE", `Addresses(${address.Id})`);
        const url = `${this.serviceUrl}/Addresses(${address.Id})`;
        return this.http.delete<Address>(url);
    }

    public getAgent(agentId: number): Observable<Agent> {
        if (!agentId) return;
        if (this.trace) this.log("GET", `Agents`);
        const url = `${this.serviceUrl}/Agents(${agentId})`;
        return this.http.get<Agent>(url, this.reqOpts);
    }

    public updateAgent(agent: Agent): Observable<Agent> {
        if (!agent || !agent.Id)
            return Observable.create((obs) => obs.next(agent));
        if (this.trace) this.log("PUT", `Agents(${agent.Id})`);
        const url = `${this.serviceUrl}/Agents(${agent.Id})`;
        return this.http.put<Agent>(url, JSON.stringify(agent), this.reqOpts);
    }

    public deleteAgent(agent: Agent): Observable<Agent> {
        if (!agent || !agent.Id) return;
        if (this.trace) this.log("DELETE", `Agents(${agent.Id})`);
        const url = `${this.serviceUrl}/Agents(${agent.Id})`;
        return this.http.delete<Agent>(url);
    }

    public getAttachment(attachmentId: number): Observable<Attachment> {
        if (!attachmentId) return;
        if (this.trace) this.log("GET", `Attachments`);
        const url = `${this.serviceUrl}/Attachments(${attachmentId})`;
        return this.http.get<Attachment>(url, this.reqOpts);
    }

    public updateAttachment(attachment: Attachment): Observable<Attachment> {
        if (!attachment || !attachment.Id)
            return Observable.create((obs) => obs.next(attachment));
        if (this.trace) this.log("PUT", `Attachments(${attachment.Id})`);
        const url = `${this.serviceUrl}/Attachments(${attachment.Id})`;
        return this.http.put<Attachment>(
            url,
            JSON.stringify(attachment),
            this.reqOpts,
        );
    }

    public deleteAttachment(attachment: Attachment): Observable<Attachment> {
        if (!attachment || !attachment.Id) return;
        if (this.trace) this.log("DELETE", `Attachments(${attachment.Id})`);
        const url = `${this.serviceUrl}/Attachments(${attachment.Id})`;
        return this.http.delete<Attachment>(url);
    }

    public getCancellation(cancellationId: number): Observable<Cancellation> {
        if (!cancellationId) return;
        if (this.trace) this.log("GET", `Cancellations`);
        const url = `${this.serviceUrl}/Cancellations(${cancellationId})`;
        return this.http.get<Cancellation>(url, this.reqOpts);
    }

    public updateCancellation(
        cancellation: Cancellation,
    ): Observable<Cancellation> {
        if (!cancellation || !cancellation.Id)
            return Observable.create((obs) => obs.next(cancellation));
        if (this.trace) this.log("PUT", `Cancellations(${cancellation.Id})`);
        const url = `${this.serviceUrl}/Cancellations(${cancellation.Id})`;
        return this.http.put<Cancellation>(
            url,
            JSON.stringify(cancellation),
            this.reqOpts,
        );
    }

    public deleteCancellation(
        cancellation: Cancellation,
    ): Observable<Cancellation> {
        if (!cancellation || !cancellation.Id) return;
        if (this.trace) this.log("DELETE", `Cancellations(${cancellation.Id})`);
        const url = `${this.serviceUrl}/Cancellations(${cancellation.Id})`;
        return this.http.delete<Cancellation>(url);
    }

    public getCompany(companyId: number): Observable<Company> {
        if (!companyId) return;
        if (this.trace) this.log("GET", `Companies`);
        const url = `${this.serviceUrl}/Companies(${companyId})`;
        return this.http.get<Company>(url, this.reqOpts);
    }

    public updateCompany(company: Company): Observable<Company> {
        if (!company || !company.Id)
            return Observable.create((obs) => obs.next(company));
        if (this.trace) this.log("PUT", `Companies(${company.Id})`);
        const url = `${this.serviceUrl}/Companies(${company.Id})`;
        return this.http.put<Company>(
            url,
            JSON.stringify(company),
            this.reqOpts,
        );
    }

    public deleteCompany(company: Company): Observable<Company> {
        if (!company || !company.Id) return;
        if (this.trace) this.log("DELETE", `Companies(${company.Id})`);
        const url = `${this.serviceUrl}/Companies(${company.Id})`;
        return this.http.delete<Company>(url);
    }

    public getConsultationDate(
        consultationDateId: number,
    ): Observable<ConsultationDate> {
        if (!consultationDateId) return;
        if (this.trace) this.log("GET", `ConsultationDates`);
        const url = `${this.serviceUrl}/ConsultationDates(${consultationDateId})`;
        return this.http.get<ConsultationDate>(url, this.reqOpts);
    }

    public updateConsultationDate(
        consultationDate: ConsultationDate,
    ): Observable<ConsultationDate> {
        if (!consultationDate || !consultationDate.Id)
            return Observable.create((obs) => obs.next(consultationDate));
        if (this.trace)
            this.log("PUT", `ConsultationDates(${consultationDate.Id})`);
        const url = `${this.serviceUrl}/ConsultationDates(${consultationDate.Id})`;
        return this.http.put<ConsultationDate>(
            url,
            JSON.stringify(consultationDate),
            this.reqOpts,
        );
    }

    public deleteConsultationDate(
        consultationDate: ConsultationDate,
    ): Observable<ConsultationDate> {
        if (!consultationDate || !consultationDate.Id) return;
        if (this.trace)
            this.log("DELETE", `ConsultationDates(${consultationDate.Id})`);
        const url = `${this.serviceUrl}/ConsultationDates(${consultationDate.Id})`;
        return this.http.delete<ConsultationDate>(url);
    }

    public getConsultationProcess(
        consultationProcessId: number,
    ): Observable<ConsultationProcess> {
        if (!consultationProcessId) return;
        if (this.trace) this.log("GET", `ConsultationProcesses`);
        const url = `${this.serviceUrl}/ConsultationProcesses(${consultationProcessId})`;
        return this.http.get<ConsultationProcess>(url, this.reqOpts);
    }

    public addConsultationProcess(
        consultationProcess: ConsultationProcess,
    ): Observable<ConsultationProcess> {
        if (!consultationProcess) return;
        if (this.trace) this.log("POST", `ConsultationProcesses`);
        const url = `${this.serviceUrl}/ConsultationProcesses`;
        return this.http.post<ConsultationProcess>(
            url,
            JSON.stringify(consultationProcess),
            this.reqOpts,
        );
    }

    public updateConsultationProcess(
        consultationProcess: ConsultationProcess,
    ): Observable<ConsultationProcess> {
        if (!consultationProcess || !consultationProcess.Id)
            return Observable.create((obs) => obs.next(consultationProcess));
        if (this.trace)
            this.log("PUT", `ConsultationProcesses(${consultationProcess.Id})`);
        const url = `${this.serviceUrl}/ConsultationProcesses(${consultationProcess.Id})`;
        return this.http.put<ConsultationProcess>(
            url,
            JSON.stringify(consultationProcess),
            this.reqOpts,
        );
    }

    public deleteConsultationProcess(
        consultationProcess: ConsultationProcess,
    ): Observable<ConsultationProcess> {
        if (!consultationProcess || !consultationProcess.Id) return;
        if (this.trace)
            this.log(
                "DELETE",
                `ConsultationProcesses(${consultationProcess.Id})`,
            );
        const url = `${this.serviceUrl}/ConsultationProcesses(${consultationProcess.Id})`;
        return this.http.delete<ConsultationProcess>(url);
    }

    public addConsultationProcess_ConsultationDates(
        consultationProcess: ConsultationProcess,
        consultationDate: ConsultationDate,
    ): Observable<ConsultationDate> {
        const cp = consultationProcess;
        const url = `${this.serviceUrl}/ConsultationProcesses(${cp.Id})/ConsultationDates`;
        // TODO server only accepts 'YYYY-MM-DD'
        const doc = {
            Date: moment(consultationDate.Date).format("YYYY-MM-DD"),
        };
        return this.http.post<ConsultationDate>(
            url,
            JSON.stringify(doc),
            this.reqOpts,
        );
    }

    public getDecision(decisionId: number): Observable<Decision> {
        if (!decisionId) return;
        if (this.trace) this.log("GET", `Decisions`);
        const url = `${this.serviceUrl}/Decisions(${decisionId})`;
        return this.http.get<Decision>(url, this.reqOpts);
    }

    public updateDecision(decision: Decision): Observable<Decision> {
        if (!decision || !decision.Id)
            return Observable.create((obs) => obs.next(decision));
        if (this.trace) this.log("PUT", `Decisions(${decision.Id})`);
        const url = `${this.serviceUrl}/Decisions(${decision.Id})`;
        return this.http.put<Decision>(
            url,
            JSON.stringify(decision),
            this.reqOpts,
        );
    }

    public deleteDecision(decision: Decision): Observable<Decision> {
        if (!decision || !decision.Id) return;
        if (this.trace) this.log("DELETE", `Decisions(${decision.Id})`);
        const url = `${this.serviceUrl}/Decisions(${decision.Id})`;
        return this.http.delete<Decision>(url);
    }

    public getDemand(demandId: number): Observable<Demand> {
        if (!demandId) return;
        if (this.trace) this.log("GET", `Demands`);
        const url = `${this.serviceUrl}/Demands(${demandId})`;
        return this.http.get<Demand>(url, this.reqOpts);
    }

    public updateDemand(demand: Demand): Observable<Demand> {
        if (!demand || !demand.Id)
            return Observable.create((obs) => obs.next(demand));
        if (this.trace) this.log("PUT", `Demands(${demand.Id})`);
        const url = `${this.serviceUrl}/Demands(${demand.Id})`;
        return this.http.put<Demand>(url, JSON.stringify(demand), this.reqOpts);
    }

    public deleteDemand(demand: Demand): Observable<Demand> {
        if (!demand || !demand.Id) return;
        if (this.trace) this.log("DELETE", `Demands(${demand.Id})`);
        const url = `${this.serviceUrl}/Demands(${demand.Id})`;
        return this.http.delete<Demand>(url);
    }

    public getDivision(divisionId: number): Observable<Division> {
        if (!divisionId) return;
        if (this.trace) this.log("GET", `Divisions`);
        const url = `${this.serviceUrl}/Divisions(${divisionId})`;
        return this.http.get<Division>(url, this.reqOpts);
    }

    public updateDivision(division: Division): Observable<Division> {
        if (!division || !division.Id)
            return Observable.create((obs) => obs.next(division));
        if (this.trace) this.log("PUT", `Divisions(${division.Id})`);
        const url = `${this.serviceUrl}/Divisions(${division.Id})`;
        return this.http.put<Division>(
            url,
            JSON.stringify(division),
            this.reqOpts,
        );
    }

    public deleteDivision(division: Division): Observable<Division> {
        if (!division || !division.Id) return;
        if (this.trace) this.log("DELETE", `Divisions(${division.Id})`);
        const url = `${this.serviceUrl}/Divisions(${division.Id})`;
        return this.http.delete<Division>(url);
    }

    public getDocument(documentId: number): Observable<Document> {
        if (!documentId) return;
        if (this.trace) this.log("GET", `Documents`);
        const url = `${this.serviceUrl}/Documents(${documentId})`;
        return this.http.get<Document>(url, this.reqOpts);
    }

    public updateDocument(document: Document): Observable<Document> {
        if (!document || !document.Id)
            return Observable.create((obs) => obs.next(document));
        if (this.trace) this.log("PUT", `Documents(${document.Id})`);
        const url = `${this.serviceUrl}/Documents(${document.Id})`;
        return this.http.put<Document>(
            url,
            JSON.stringify(document),
            this.reqOpts,
        );
    }

    public deleteDocument(document: Document): Observable<Document> {
        if (!document || !document.Id) return;
        if (this.trace) this.log("DELETE", `Documents(${document.Id})`);
        const url = `${this.serviceUrl}/Documents(${document.Id})`;
        return this.http.delete<Document>(url);
    }

    public getEvent(eventId: number): Observable<Event> {
        if (!eventId) return;
        if (this.trace) this.log("GET", `Events`);
        const url = `${this.serviceUrl}/Events(${eventId})`;
        return this.http.get<Event>(url, this.reqOpts);
    }

    public updateEvent(event: Event): Observable<Event> {
        if (!event || !event.Id)
            return Observable.create((obs) => obs.next(event));
        if (this.trace) this.log("PUT", `Events(${event.Id})`);
        const url = `${this.serviceUrl}/Events(${event.Id})`;
        return this.http.put<Event>(url, JSON.stringify(event), this.reqOpts);
    }

    public deleteEvent(event: Event): Observable<Event> {
        if (!event || !event.Id) return;
        if (this.trace) this.log("DELETE", `Events(${event.Id})`);
        const url = `${this.serviceUrl}/Events(${event.Id})`;
        return this.http.delete<Event>(url);
    }

    public getExemption(exemptionId: number): Observable<Exemption> {
        if (!exemptionId) return;
        if (this.trace) this.log("GET", `Exemptions`);
        const url = `${this.serviceUrl}/Exemptions(${exemptionId})`;
        return this.http.get<Exemption>(url, this.reqOpts);
    }

    public updateExemption(exemption: Exemption): Observable<Exemption> {
        if (!exemption || !exemption.Id)
            return Observable.create((obs) => obs.next(exemption));
        if (this.trace) this.log("PUT", `Exemptions(${exemption.Id})`);
        const url = `${this.serviceUrl}/Exemptions(${exemption.Id})`;
        return this.http.put<Exemption>(
            url,
            JSON.stringify(exemption),
            this.reqOpts,
        );
    }

    public deleteExemption(exemption: Exemption): Observable<Exemption> {
        if (!exemption || !exemption.Id) return;
        if (this.trace) this.log("DELETE", `Exemptions(${exemption.Id})`);
        const url = `${this.serviceUrl}/Exemptions(${exemption.Id})`;
        return this.http.delete<Exemption>(url);
    }

    public getGlobalOccasion(
        globalOccasionId: number,
    ): Observable<GlobalOccasion> {
        if (!globalOccasionId) return;
        if (this.trace) this.log("GET", `GlobalOccasions`);
        const url = `${this.serviceUrl}/GlobalOccasions(${globalOccasionId})`;
        return this.http.get<GlobalOccasion>(url, this.reqOpts);
    }

    public updateGlobalOccasion(
        globalOccasion: GlobalOccasion,
    ): Observable<GlobalOccasion> {
        if (!globalOccasion || !globalOccasion.Id)
            return Observable.create((obs) => obs.next(globalOccasion));
        if (this.trace)
            this.log("PUT", `GlobalOccasions(${globalOccasion.Id})`);
        const url = `${this.serviceUrl}/GlobalOccasions(${globalOccasion.Id})`;
        return this.http.put<GlobalOccasion>(
            url,
            JSON.stringify(globalOccasion),
            this.reqOpts,
        );
    }

    public deleteGlobalOccasion(
        globalOccasion: GlobalOccasion,
    ): Observable<GlobalOccasion> {
        if (!globalOccasion || !globalOccasion.Id) return;
        if (this.trace)
            this.log("DELETE", `GlobalOccasions(${globalOccasion.Id})`);
        const url = `${this.serviceUrl}/GlobalOccasions(${globalOccasion.Id})`;
        return this.http.delete<GlobalOccasion>(url);
    }

    public addGlobalOccasion_ConsultationDates(
        globalOccasion: GlobalOccasion,
        consultationDate: ConsultationDate,
    ): Observable<GlobalOccasion> {
        if (!globalOccasion || !globalOccasion.Id) return;
        if (this.trace)
            this.log(
                " POST",
                `GlobalOccasions(${globalOccasion.Id})/ConsultationDates`,
            );
        const url = `${this.serviceUrl}/GlobalOccasions(${globalOccasion.Id})/ConsultationDates`;
        const doc = {
            Date: moment(consultationDate.Date).format("YYYY-MM-DD"),
        };
        return this.http.post<GlobalOccasion>(
            url,
            JSON.stringify(doc),
            this.reqOpts,
        );
    }

    public addGlobalOccasion_Attachments(
        globalOccasion: GlobalOccasion,
        attachment: Attachment,
    ): Observable<Attachment> {
        if (!globalOccasion || !globalOccasion.Id) return;
        if (this.trace)
            this.log(
                "POST",
                `GlobalOccasions(${globalOccasion.Id})/Attachments`,
            );
        const url = `${this.serviceUrl}/GlobalOccasions(${globalOccasion.Id})/Attachments`;
        return this.http.post<Attachment>(
            url,
            JSON.stringify(attachment),
            this.reqOpts,
        );
    }

    public addGlobalOccasion_GlobalOccasionSections(
        globalOccasion: GlobalOccasion,
        globalOccasionSection: GlobalOccasionSection,
    ): Observable<GlobalOccasionSection> {
        if (!globalOccasion || !globalOccasion.Id) return;
        if (this.trace)
            this.log(
                "POST",
                `GlobalOccasions(${globalOccasion.Id})/GlobalOccasionSections`,
            );
        const url = `${this.serviceUrl}/GlobalOccasions(${globalOccasion.Id})/GlobalOccasionSections?$expand=*($levels=20)`;
        return this.http.post<GlobalOccasionSection>(
            url,
            JSON.stringify(globalOccasionSection),
            this.reqOpts,
        );
    }

    public getGlobalOccasionEntry(
        globalOccasionEntryId: number,
    ): Observable<GlobalOccasionEntry> {
        if (!globalOccasionEntryId) return;
        if (this.trace) this.log("GET", `GlobalOccasionEntries`);
        const url = `${this.serviceUrl}/GlobalOccasionEntries(${globalOccasionEntryId})`;
        return this.http.get<GlobalOccasionEntry>(url, this.reqOpts);
    }

    public updateGlobalOccasionEntry(
        globalOccasionEntry: GlobalOccasionEntry,
    ): Observable<GlobalOccasionEntry> {
        if (!globalOccasionEntry || !globalOccasionEntry.Id)
            return Observable.create((obs) => obs.next(globalOccasionEntry));
        if (this.trace)
            this.log("PUT", `GlobalOccasionEntries(${globalOccasionEntry.Id})`);
        const url = `${this.serviceUrl}/GlobalOccasionEntries(${globalOccasionEntry.Id})`;
        const doc: object = Object.assign(globalOccasionEntry);
        if (doc["ConsultingDate"]) {
            const date = moment(doc["ConsultingDate"]).format("YYYY-MM-DD");
            doc["ConsultingDate"] = date;
        }
        return this.http.put<GlobalOccasionEntry>(
            url,
            JSON.stringify(globalOccasionEntry),
            this.reqOpts,
        );
    }

    public deleteGlobalOccasionEntry(
        globalOccasionEntry: GlobalOccasionEntry,
    ): Observable<GlobalOccasionEntry> {
        if (!globalOccasionEntry || !globalOccasionEntry.Id) return;
        if (this.trace)
            this.log(
                "DELETE",
                `GlobalOccasionEntries(${globalOccasionEntry.Id})`,
            );
        const url = `${this.serviceUrl}/GlobalOccasionEntries(${globalOccasionEntry.Id})`;
        return this.http.delete<GlobalOccasionEntry>(url);
    }

    public getGlobalOccasionSection(
        globalOccasionSectionId: number,
    ): Observable<GlobalOccasionSection> {
        if (!globalOccasionSectionId) return;
        if (this.trace) this.log("GET", `GlobalOccasionSections`);
        const url = `${this.serviceUrl}/GlobalOccasionSections(${globalOccasionSectionId})`;
        return this.http.get<GlobalOccasionSection>(url, this.reqOpts);
    }

    public updateGlobalOccasionSection(
        globalOccasionSection: GlobalOccasionSection,
    ): Observable<GlobalOccasionSection> {
        if (!globalOccasionSection || !globalOccasionSection.Id)
            return Observable.create((obs) => obs.next(globalOccasionSection));
        if (this.trace)
            this.log(
                "PUT",
                `GlobalOccasionSections(${globalOccasionSection.Id})`,
            );
        const url = `${this.serviceUrl}/GlobalOccasionSections(${globalOccasionSection.Id})`;
        return this.http.put<GlobalOccasionSection>(
            url,
            JSON.stringify(globalOccasionSection),
            this.reqOpts,
        );
    }

    public deleteGlobalOccasionSection(
        globalOccasionSection: GlobalOccasionSection,
    ): Observable<GlobalOccasionSection> {
        if (!globalOccasionSection || !globalOccasionSection.Id) return;
        if (this.trace)
            this.log(
                "DELETE",
                `GlobalOccasionSections(${globalOccasionSection.Id})`,
            );
        const url = `${this.serviceUrl}/GlobalOccasionSections(${globalOccasionSection.Id})`;
        return this.http.delete<GlobalOccasionSection>(url);
    }

    public addGlobalOccasionSection_GlobalOccasionEntries(
        globalOccasionSection: GlobalOccasionSection,
        globalOccasionEntry: GlobalOccasionEntry,
    ): Observable<GlobalOccasionEntry> {
        if (!globalOccasionSection || !globalOccasionSection.Id) return;
        if (this.trace)
            this.log(
                "POST",
                `GlobalOccasionSections(${globalOccasionSection.Id})/GlobalOccasionEntries`,
            );
        const url = `${this.serviceUrl}/GlobalOccasionSections(${globalOccasionSection.Id})/GlobalOccasionEntries`;
        return this.http.post<GlobalOccasionEntry>(
            url,
            JSON.stringify(globalOccasionEntry),
            this.reqOpts,
        );
    }

    public getIndividual(individualId: number): Observable<Individual> {
        if (!individualId) return;
        if (this.trace) this.log("GET", `Individuals`);
        const url = `${this.serviceUrl}/Individuals(${individualId})`;
        return this.http.get<Individual>(url, this.reqOpts);
    }

    public updateIndividual(individual: Individual): Observable<Individual> {
        if (!individual || !individual.Id)
            return Observable.create((obs) => obs.next(individual));
        if (this.trace) this.log("PUT", `Individuals(${individual.Id})`);
        const url = `${this.serviceUrl}/Individuals(${individual.Id})`;
        return this.http.put<Individual>(
            url,
            JSON.stringify(individual),
            this.reqOpts,
        );
    }

    public deleteIndividual(individual: Individual): Observable<Individual> {
        if (!individual || !individual.Id) return;
        if (this.trace) this.log("DELETE", `Individuals(${individual.Id})`);
        const url = `${this.serviceUrl}/Individuals(${individual.Id})`;
        return this.http.delete<Individual>(url);
    }

    public getOccasionOccasionEntry(
        occasionOccasionEntryId: number,
    ): Observable<OccasionOccasionEntry> {
        if (!occasionOccasionEntryId) return;
        if (this.trace) this.log("GET", `OccasionOccasionEntries`);
        const url = `${this.serviceUrl}/OccasionOccasionEntries(${occasionOccasionEntryId})`;
        return this.http.get<OccasionOccasionEntry>(url, this.reqOpts);
    }

    public updateOccasionOccasionEntry(
        occasionOccasionEntry: OccasionOccasionEntry,
    ): Observable<OccasionOccasionEntry> {
        if (!occasionOccasionEntry || !occasionOccasionEntry.Id)
            return Observable.create((obs) => obs.next(occasionOccasionEntry));
        if (this.trace)
            this.log(
                "PUT",
                `OccasionOccasionEntries(${occasionOccasionEntry.Id})`,
            );
        const url = `${this.serviceUrl}/OccasionOccasionEntries(${occasionOccasionEntry.Id})`;
        return this.http.put<OccasionOccasionEntry>(
            url,
            JSON.stringify(occasionOccasionEntry),
            this.reqOpts,
        );
    }

    public deleteOccasionOccasionEntry(
        occasionOccasionEntry: OccasionOccasionEntry,
    ): Observable<OccasionOccasionEntry> {
        if (!occasionOccasionEntry || !occasionOccasionEntry.Id) return;
        if (this.trace)
            this.log(
                "DELETE",
                `OccasionOccasionEntries(${occasionOccasionEntry.Id})`,
            );
        const url = `${this.serviceUrl}/OccasionOccasionEntries(${occasionOccasionEntry.Id})`;
        return this.http.delete<OccasionOccasionEntry>(url);
    }

    public getOffer(offerId: number): Observable<Offer> {
        if (!offerId) return;
        if (this.trace) this.log("GET", `Offers`);
        const url = `${this.serviceUrl}/Offers(${offerId})`;
        return this.http.get<Offer>(url, this.reqOpts);
    }

    public updateOffer(offer: Offer): Observable<Offer> {
        if (!offer || !offer.Id)
            return Observable.create((obs) => obs.next(offer));
        if (this.trace) this.log("PUT", `Offers(${offer.Id})`);
        const url = `${this.serviceUrl}/Offers(${offer.Id})`;
        return this.http.put<Offer>(url, JSON.stringify(offer), this.reqOpts);
    }

    public deleteOffer(offer: Offer): Observable<Offer> {
        if (!offer || !offer.Id) return;
        if (this.trace) this.log("DELETE", `Offers(${offer.Id})`);
        const url = `${this.serviceUrl}/Offers(${offer.Id})`;
        return this.http.delete<Offer>(url);
    }

    public addOffer_Divisions(
        offer: Offer,
        division: Division,
    ): Observable<Division> {
        if (!offer || !offer.Id) return;
        if (this.trace) this.log("POST", `Offers(${offer.Id})/Divisions`);
        const url = `${this.serviceUrl}/Offers(${offer.Id})/Divisions`;
        return this.http.post<Division>(
            url,
            JSON.stringify(division),
            this.reqOpts,
        );
    }

    public getParticipant(participantId: number): Observable<Participant> {
        if (!participantId) return;
        if (this.trace) this.log("GET", `Participants`);
        const url = `${this.serviceUrl}/Participants(${participantId})`;
        return this.http.get<Participant>(url, this.reqOpts);
    }

    public updateParticipant(
        participant: Participant,
    ): Observable<Participant> {
        if (!participant || !participant.Id)
            return Observable.create((obs) => obs.next(participant));
        if (this.trace) this.log("PUT", `Participants(${participant.Id})`);
        const url = `${this.serviceUrl}/Participants(${participant.Id})`;
        return this.http.put<Participant>(
            url,
            JSON.stringify(participant),
            this.reqOpts,
        );
    }

    public deleteParticipant(
        participant: Participant,
    ): Observable<Participant> {
        if (!participant || !participant.Id) return;
        if (this.trace) this.log("DELETE", `Participants(${participant.Id})`);
        const url = `${this.serviceUrl}/Participants(${participant.Id})`;
        return this.http.delete<Participant>(url);
    }

    public getProductOccasionEntry(
        productOccasionEntryId: number,
    ): Observable<ProductOccasionEntry> {
        if (!productOccasionEntryId) return;
        if (this.trace) this.log("GET", `ProductOccasionEntries`);
        const url = `${this.serviceUrl}/ProductOccasionEntries(${productOccasionEntryId})`;
        return this.http.get<ProductOccasionEntry>(url, this.reqOpts);
    }

    public updateProductOccasionEntry(
        productOccasionEntry: ProductOccasionEntry,
    ): Observable<ProductOccasionEntry> {
        if (!productOccasionEntry || !productOccasionEntry.Id)
            return Observable.create((obs) => obs.next(productOccasionEntry));
        if (this.trace)
            this.log(
                "PUT",
                `ProductOccasionEntries(${productOccasionEntry.Id})`,
            );
        const url = `${this.serviceUrl}/ProductOccasionEntries(${productOccasionEntry.Id})`;
        return this.http.put<ProductOccasionEntry>(
            url,
            JSON.stringify(productOccasionEntry),
            this.reqOpts,
        );
    }

    public deleteProductOccasionEntry(
        productOccasionEntry: ProductOccasionEntry,
    ): Observable<ProductOccasionEntry> {
        if (!productOccasionEntry || !productOccasionEntry.Id) return;
        if (this.trace)
            this.log(
                "DELETE",
                `ProductOccasionEntries(${productOccasionEntry.Id})`,
            );
        const url = `${this.serviceUrl}/ProductOccasionEntries(${productOccasionEntry.Id})`;
        return this.http.delete<ProductOccasionEntry>(url);
    }

    public getProductOccasionSection(
        productOccasionSectionId: number,
    ): Observable<ProductOccasionSection> {
        if (!productOccasionSectionId) return;
        if (this.trace) this.log("GET", `ProductOccasionSections`);
        const url = `${this.serviceUrl}/ProductOccasionSections(${productOccasionSectionId})`;
        return this.http.get<ProductOccasionSection>(url, this.reqOpts);
    }

    public updateProductOccasionSection(
        productOccasionSection: ProductOccasionSection,
    ): Observable<ProductOccasionSection> {
        if (!productOccasionSection || !productOccasionSection.Id)
            return Observable.create((obs) => obs.next(productOccasionSection));
        if (this.trace)
            this.log(
                "PUT",
                `ProductOccasionSections(${productOccasionSection.Id})`,
            );
        const url = `${this.serviceUrl}/ProductOccasionSections(${productOccasionSection.Id})`;
        return this.http.put<ProductOccasionSection>(
            url,
            JSON.stringify(productOccasionSection),
            this.reqOpts,
        );
    }

    public deleteProductOccasionSection(
        productOccasionSection: ProductOccasionSection,
    ): Observable<ProductOccasionSection> {
        if (!productOccasionSection || !productOccasionSection.Id) return;
        if (this.trace)
            this.log(
                "DELETE",
                `ProductOccasionSections(${productOccasionSection.Id})`,
            );
        const url = `${this.serviceUrl}/ProductOccasionSections(${productOccasionSection.Id})`;
        return this.http.delete<ProductOccasionSection>(url);
    }

    public addProductOccasionSection_ProductOccasionEntries(
        productOccasionSection: ProductOccasionSection,
        productOccasionEntry: ProductOccasionEntry,
    ): Observable<ProductOccasionEntry> {
        if (!productOccasionSection || !productOccasionSection.Id) return;
        if (this.trace)
            this.log(
                "POST",
                `ProductOccasionSections(${productOccasionSection.Id})/ProductOccasionEntries`,
            );
        const url = `${this.serviceUrl}/ProductOccasionSections(${productOccasionSection.Id})/ProductOccasionEntries`;
        return this.http.post<ProductOccasionEntry>(
            url,
            JSON.stringify(productOccasionEntry),
            this.reqOpts,
        );
    }

    public getProtocol(protocolId: number): Observable<Protocol> {
        if (!protocolId) return;
        if (this.trace) this.log("GET", `Protocols`);
        const url = `${this.serviceUrl}/Protocols(${protocolId})`;
        return this.http.get<Protocol>(url, this.reqOpts);
    }

    public updateProtocol(protocol: Protocol): Observable<Protocol> {
        if (!protocol || !protocol.Id)
            return Observable.create((obs) => obs.next(protocol));
        if (this.trace) this.log("PUT", `Protocols(${protocol.Id})`);
        const url = `${this.serviceUrl}/Protocols(${protocol.Id})`;
        return this.http.put<Protocol>(
            url,
            JSON.stringify(protocol),
            this.reqOpts,
        );
    }

    public deleteProtocol(protocol: Protocol): Observable<Protocol> {
        if (!protocol || !protocol.Id) return;
        if (this.trace) this.log("DELETE", `Protocols(${protocol.Id})`);
        const url = `${this.serviceUrl}/Protocols(${protocol.Id})`;
        return this.http.delete<Protocol>(url);
    }

    public addProtocol_Offers(
        protocol: Protocol,
        offer: Offer,
    ): Observable<Offer> {
        if (!protocol || !protocol.Id) return;
        if (this.trace) this.log("POST", `Protocols(${protocol.Id})/Offers`);
        const url = `${this.serviceUrl}/Protocols(${protocol.Id})/Offers`;
        return this.http.post<Offer>(url, JSON.stringify(offer), this.reqOpts);
    }

    public addProtocol_Attachments(
        protocol: Protocol,
        attachment: Attachment,
    ): Observable<Attachment> {
        if (!protocol || !protocol.Id) return;
        if (this.trace)
            this.log("POST", `Protocols(${protocol.Id})/Attachments`);
        const url = `${this.serviceUrl}/Protocols(${protocol.Id})/Attachments`;
        return this.http.post<Attachment>(
            url,
            JSON.stringify(attachment),
            this.reqOpts,
        );
    }

    public addProtocol_Participants(
        protocol: Protocol,
        participant: Participant,
    ): Observable<Participant> {
        if (!protocol || !protocol.Id) return;
        if (this.trace)
            this.log("POST", `Protocols(${protocol.Id})/Participants`);
        const url = `${this.serviceUrl}/Protocols(${protocol.Id})/Participants`;
        return this.http.post<Participant>(
            url,
            JSON.stringify(participant),
            this.reqOpts,
        );
    }

    public addProtocol_ProductOccasionSections(
        protocol: Protocol,
        productOccasionSection: ProductOccasionSection,
    ): Observable<ProductOccasionSection> {
        if (!protocol || !protocol.Id) return;
        if (this.trace)
            this.log(
                "POST",
                `Protocols(${protocol.Id})/ProductOccasionSections`,
            );
        const url = `${this.serviceUrl}/Protocols(${protocol.Id})/ProductOccasionSections`;
        return this.http.post<ProductOccasionSection>(
            url,
            JSON.stringify(productOccasionSection),
            this.reqOpts,
        );
    }

    public addProtocol_OccasionOccasionEntries(
        protocol: Protocol,
        occasionOccasionEntry: OccasionOccasionEntry,
    ): Observable<OccasionOccasionEntry> {
        if (!protocol || !protocol.Id) return;
        if (this.trace)
            this.log(
                "POST",
                `Protocols(${protocol.Id})/OccasionOccasionEntries`,
            );
        const url = `${this.serviceUrl}/Protocols(${protocol.Id})/OccasionOccasionEntries`;
        return this.http.post<OccasionOccasionEntry>(
            url,
            JSON.stringify(occasionOccasionEntry),
            this.reqOpts,
        );
    }

    public setProtocol_GlobalOccasion(
        protocol: Protocol,
        globalOccasion: GlobalOccasion,
    ): Observable<GlobalOccasion> {
        if (!protocol || !protocol.Id) return;
        if (this.trace)
            this.log("POST", `Protocols(${protocol.Id})/GlobalOccasion`);
        const url = `${this.serviceUrl}/Protocols(${protocol.Id})/GlobalOccasion`;
        return this.http.put<GlobalOccasion>(
            url,
            JSON.stringify(globalOccasion),
            this.reqOpts,
        );
    }

    public getRecommendation(
        recommendationId: number,
    ): Observable<Recommendation> {
        if (!recommendationId) return;
        if (this.trace) this.log("GET", `Recommendations`);
        const url = `${this.serviceUrl}/Recommendations(${recommendationId})`;
        return this.http.get<Recommendation>(url, this.reqOpts);
    }

    public updateRecommendation(
        recommendation: Recommendation,
    ): Observable<Recommendation> {
        if (!recommendation || !recommendation.Id)
            return Observable.create((obs) => obs.next(recommendation));
        if (this.trace)
            this.log("PUT", `Recommendations(${recommendation.Id})`);
        const url = `${this.serviceUrl}/Recommendations(${recommendation.Id})`;
        return this.http.put<Recommendation>(
            url,
            JSON.stringify(recommendation),
            this.reqOpts,
        );
    }

    public deleteRecommendation(
        recommendation: Recommendation,
    ): Observable<Recommendation> {
        if (!recommendation || !recommendation.Id) return;
        if (this.trace)
            this.log("DELETE", `Recommendations(${recommendation.Id})`);
        const url = `${this.serviceUrl}/Recommendations(${recommendation.Id})`;
        return this.http.delete<Recommendation>(url);
    }

    public addRecommendation_RecommendedCancellations(
        recommendation: Recommendation,
        recommendedCancellation: Cancellation,
    ): Observable<Cancellation> {
        if (!recommendation || !recommendation.Id) return;
        if (this.trace)
            this.log(
                "POST",
                `Recommendations(${recommendation.Id})/RecommendedCancellations`,
            );
        const url = `${this.serviceUrl}/Recommendations(${recommendation.Id})/RecommendedCancellations`;
        return this.http.post<Cancellation>(
            url,
            JSON.stringify(recommendedCancellation),
            this.reqOpts,
        );
    }

    public addRecommendation_RecommendedExemptions(
        recommendation: Recommendation,
        recommendedExemption: Exemption,
    ): Observable<Exemption> {
        if (!recommendation || !recommendation.Id) return;
        if (this.trace)
            this.log(
                "POST",
                `Recommendations(${recommendation.Id})/RecommendedExemptions`,
            );
        const url = `${this.serviceUrl}/Recommendations(${recommendation.Id})/RecommendedExemptions`;
        return this.http.post<Exemption>(
            url,
            JSON.stringify(recommendedExemption),
            this.reqOpts,
        );
    }

    public getTextblock(textblockId: number): Observable<Textblock> {
        if (!textblockId) return;
        if (this.trace) this.log("GET", `Textblocks`);
        const url = `${this.serviceUrl}/Textblocks(${textblockId})`;
        return this.http.get<Textblock>(url, this.reqOpts);
    }

    public updateTextblock(textblock: Textblock): Observable<Textblock> {
        if (!textblock || !textblock.Id)
            return Observable.create((obs) => obs.next(textblock));
        if (this.trace) this.log("PUT", `Textblocks(${textblock.Id})`);
        const url = `${this.serviceUrl}/Textblocks(${textblock.Id})`;
        return this.http.put<Textblock>(
            url,
            JSON.stringify(textblock),
            this.reqOpts,
        );
    }

    public deleteTextblock(textblock: Textblock): Observable<Textblock> {
        if (!textblock || !textblock.Id) return;
        if (this.trace) this.log("DELETE", `Textblocks(${textblock.Id})`);
        const url = `${this.serviceUrl}/Textblocks(${textblock.Id})`;
        return this.http.delete<Textblock>(url);
    }

    public getTrackingText(trackingTextId: number): Observable<TrackingText> {
        if (!trackingTextId) return;
        if (this.trace) this.log("GET", `TrackingTexts`);
        const url = `${this.serviceUrl}/TrackingTexts(${trackingTextId})`;
        return this.http.get<TrackingText>(url, this.reqOpts);
    }

    public updateTrackingText(
        trackingText: TrackingText,
    ): Observable<TrackingText> {
        if (!trackingText || !trackingText.Id)
            return Observable.create((obs) => obs.next(trackingText));
        if (this.trace) this.log("PUT", `TrackingTexts(${trackingText.Id})`);
        const url = `${this.serviceUrl}/TrackingTexts(${trackingText.Id})`;
        return this.http.put<TrackingText>(
            url,
            JSON.stringify(trackingText),
            this.reqOpts,
        );
    }

    public deleteTrackingText(
        trackingText: TrackingText,
    ): Observable<TrackingText> {
        if (!trackingText || !trackingText.Id) return;
        if (this.trace) this.log("DELETE", `TrackingTexts(${trackingText.Id})`);
        const url = `${this.serviceUrl}/TrackingTexts(${trackingText.Id})`;
        return this.http.delete<TrackingText>(url);
    }

    public addTrackingText_Events(
        trackingText: TrackingText,
        event: Event,
    ): Observable<Event> {
        if (!trackingText || !trackingText.Id) return;
        if (this.trace)
            this.log("POST", `TrackingTexts(${trackingText.Id})/Events`);
        const url = `${this.serviceUrl}/TrackingTexts(${trackingText.Id})/Events`;
        return this.http.post<Event>(url, JSON.stringify(event), this.reqOpts);
    }
}
